/**
 * NS theme — composable per-component override-maps voor MUI v4.
 *
 * NOTE (M1, PR 469097 review): de splitsing tussen `theme.ts` en
 * `theme-overrides.ts` is **puur cosmetisch**. Er is geen runtime- of
 * architectuur-grens; de bestanden worden in dezelfde import-graph
 * samengevoegd door `createUnifiedThemeFromV4` in `theme.ts`. De split
 * bestaat alleen om import-noise te bundelen en de file size onder de
 * 500-regel review-threshold te houden (CP3b batch 4). Beweeg gerust
 * exports tussen beide files als het de leesbaarheid verbetert.
 *
 * `MuiDrawerProps` is een `props`-only export en wordt apart geïmporteerd in
 * theme.ts.
 */

import {
  MuiAppBarDarkOverrides,
  MuiAppBarLightOverrides,
} from './components/app-bar/mui-app-bar-overrides';
import {
  MuiButtonDarkOverrides,
  MuiButtonLightOverrides,
} from './components/button/mui-button-overrides';
import {
  MuiCardDarkOverrides,
  MuiCardLightOverrides,
} from './components/card/mui-card-overrides';
import {
  MuiPaperDarkOverrides,
  MuiPaperLightOverrides,
} from './components/card/mui-paper-overrides';
import {
  MuiCardActionsDarkOverrides,
  MuiCardActionsLightOverrides,
} from './components/card-actions/mui-card-actions-overrides';
import {
  MuiCardContentDarkOverrides,
  MuiCardContentLightOverrides,
} from './components/card-content/mui-card-content-overrides';
import {
  MuiCardHeaderDarkOverrides,
  MuiCardHeaderLightOverrides,
} from './components/card-header/mui-card-header-overrides';
import {
  MuiCheckboxDarkOverrides,
  MuiCheckboxLightOverrides,
} from './components/checkbox/mui-checkbox-overrides';
import { MuiChipInteractiveRootExtension } from './components/chip/mui-chip-overrides';
import {
  BackstageContentDarkOverrides,
  BackstageContentLightOverrides,
} from './components/content/backstage-content-overrides';
import {
  MuiDrawerDarkOverrides,
  MuiDrawerLightOverrides,
} from './components/drawer/mui-drawer-overrides';
import {
  MuiFormControlLabelDarkOverrides,
  MuiFormControlLabelLightOverrides,
} from './components/form-control-label/mui-form-control-label-overrides';
import {
  MuiFormHelperTextDarkOverrides,
  MuiFormHelperTextLightOverrides,
} from './components/form-helper-text/mui-form-helper-text-overrides';
import {
  BackstageHeaderDarkOverrides,
  BackstageHeaderLightOverrides,
} from './components/header/backstage-header-overrides';
import {
  BackstageHeaderTabsDarkOverrides,
  BackstageHeaderTabsLightOverrides,
} from './components/header-tabs/backstage-header-tabs-overrides';
import {
  MuiIconButtonDarkOverrides,
  MuiIconButtonLightOverrides,
} from './components/icon-button/mui-icon-button-overrides';
import {
  MuiInputBaseDarkOverrides,
  MuiInputBaseLightOverrides,
} from './components/input-base/mui-input-base-overrides';
import {
  MuiInputLabelDarkOverrides,
  MuiInputLabelLightOverrides,
} from './components/input-label/mui-input-label-overrides';
import {
  BackstageItemCardGridDarkOverrides,
  BackstageItemCardGridLightOverrides,
} from './components/item-card-grid/backstage-item-card-grid-overrides';
import {
  MuiLinkDarkOverrides,
  MuiLinkLightOverrides,
} from './components/link/mui-link-overrides';
import {
  MuiListDarkOverrides,
  MuiListLightOverrides,
} from './components/list/mui-list-overrides';
import {
  MuiListItemDarkOverrides,
  MuiListItemLightOverrides,
} from './components/list-item/mui-list-item-overrides';
import {
  MuiListItemIconDarkOverrides,
  MuiListItemIconLightOverrides,
} from './components/list-item-icon/mui-list-item-icon-overrides';
import {
  MuiListItemTextDarkOverrides,
  MuiListItemTextLightOverrides,
} from './components/list-item-text/mui-list-item-text-overrides';
import {
  MuiMenuItemDarkOverrides,
  MuiMenuItemLightOverrides,
} from './components/menu-item/mui-menu-item-overrides';
import {
  MuiNativeSelectDarkOverrides,
  MuiNativeSelectLightOverrides,
} from './components/native-select/mui-native-select-overrides';
import {
  MuiRadioDarkOverrides,
  MuiRadioLightOverrides,
} from './components/radio/mui-radio-overrides';
import {
  MuiSelectDarkOverrides,
  MuiSelectLightOverrides,
} from './components/select/mui-select-overrides';
import {
  MuiSwitchDarkOverrides,
  MuiSwitchLightOverrides,
} from './components/switch/mui-switch-overrides';
import {
  MuiTabDarkOverrides,
  MuiTabLightOverrides,
} from './components/tab/mui-tab-overrides';
import {
  MuiTableDarkOverrides,
  MuiTableLightOverrides,
} from './components/table/mui-table-overrides';
import {
  MuiTableCellDarkOverrides,
  MuiTableCellLightOverrides,
} from './components/table-cell/mui-table-cell-overrides';
import {
  MuiTableRowDarkOverrides,
  MuiTableRowLightOverrides,
} from './components/table-row/mui-table-row-overrides';
import {
  MuiTableSortLabelDarkOverrides,
  MuiTableSortLabelLightOverrides,
} from './components/table-sort-label/mui-table-sort-label-overrides';
import {
  MuiTabsDarkOverrides,
  MuiTabsLightOverrides,
} from './components/tabs/mui-tabs-overrides';
import {
  MuiChipDarkOverrides,
  MuiChipLightOverrides,
} from './components/tag/mui-chip-as-tag-overrides';
import {
  MuiOutlinedInputDarkOverrides,
  MuiOutlinedInputLightOverrides,
} from './components/text-field/mui-outlined-input-overrides';
import {
  MuiTextFieldMultilineDarkOverrides,
  MuiTextFieldMultilineLightOverrides,
} from './components/text-field/mui-text-field-multiline-overrides';
import {
  MuiTextFieldRootDarkOverrides,
  MuiTextFieldRootLightOverrides,
} from './components/text-field/mui-text-field-root-overrides';
import {
  MuiTextFieldSearchDarkOverrides,
  MuiTextFieldSearchLightOverrides,
} from './components/text-field/mui-text-field-search-overrides';
import {
  MuiToggleButtonDarkOverrides,
  MuiToggleButtonGroupDarkOverrides,
  MuiToggleButtonGroupLightOverrides,
  MuiToggleButtonLightOverrides,
} from './components/toggle-button/mui-toggle-button-overrides';
import {
  MuiToolbarDarkOverrides,
  MuiToolbarLightOverrides,
} from './components/toolbar/mui-toolbar-overrides';
import {
  MuiTooltipDarkOverrides,
  MuiTooltipLightOverrides,
} from './components/tooltip/mui-tooltip-overrides';
import {
  CatalogReactUserListPickerDarkOverrides,
  CatalogReactUserListPickerLightOverrides,
} from './components/user-list-picker/catalog-react-user-list-picker-overrides';

// MuiTextField has only `root` as classKey, so multiline en search-type wrappers
// worden gemerged in een enkele MuiTextField.root via attribute-selectors.
const MuiTextFieldLightOverrides = {
  root: {
    ...MuiTextFieldRootLightOverrides.root,
    ...MuiTextFieldMultilineLightOverrides.root,
    ...MuiTextFieldSearchLightOverrides.root,
  },
} as const;

const MuiTextFieldDarkOverrides = {
  root: {
    ...MuiTextFieldRootDarkOverrides.root,
    ...MuiTextFieldMultilineDarkOverrides.root,
    ...MuiTextFieldSearchDarkOverrides.root,
  },
} as const;

export const NS_LIGHT_OVERRIDES = {
  BackstageContent: BackstageContentLightOverrides,
  BackstageHeader: BackstageHeaderLightOverrides,
  BackstageHeaderTabs: BackstageHeaderTabsLightOverrides,
  BackstageItemCardGrid: BackstageItemCardGridLightOverrides,
  CatalogReactUserListPicker: CatalogReactUserListPickerLightOverrides,
  MuiAppBar: MuiAppBarLightOverrides,
  MuiButton: MuiButtonLightOverrides,
  MuiCard: MuiCardLightOverrides,
  MuiCardActions: MuiCardActionsLightOverrides,
  MuiCardContent: MuiCardContentLightOverrides,
  MuiCardHeader: MuiCardHeaderLightOverrides,
  MuiCheckbox: MuiCheckboxLightOverrides,
  MuiChip: {
    ...MuiChipLightOverrides,
    root: {
      ...MuiChipLightOverrides.root,
      ...MuiChipInteractiveRootExtension,
    },
  },
  MuiDrawer: MuiDrawerLightOverrides,
  MuiFormControlLabel: MuiFormControlLabelLightOverrides,
  MuiFormHelperText: MuiFormHelperTextLightOverrides,
  MuiIconButton: MuiIconButtonLightOverrides,
  MuiInputBase: MuiInputBaseLightOverrides,
  MuiInputLabel: MuiInputLabelLightOverrides,
  MuiLink: MuiLinkLightOverrides,
  MuiList: MuiListLightOverrides,
  MuiListItem: MuiListItemLightOverrides,
  MuiListItemIcon: MuiListItemIconLightOverrides,
  MuiListItemText: MuiListItemTextLightOverrides,
  MuiMenuItem: MuiMenuItemLightOverrides,
  MuiNativeSelect: MuiNativeSelectLightOverrides,
  MuiOutlinedInput: MuiOutlinedInputLightOverrides,
  MuiPaper: MuiPaperLightOverrides,
  MuiRadio: MuiRadioLightOverrides,
  MuiSelect: MuiSelectLightOverrides,
  MuiSwitch: MuiSwitchLightOverrides,
  MuiTab: MuiTabLightOverrides,
  MuiTable: MuiTableLightOverrides,
  MuiTableCell: MuiTableCellLightOverrides,
  MuiTableRow: MuiTableRowLightOverrides,
  MuiTableSortLabel: MuiTableSortLabelLightOverrides,
  MuiTabs: MuiTabsLightOverrides,
  MuiTextField: MuiTextFieldLightOverrides,
  MuiToggleButton: MuiToggleButtonLightOverrides,
  MuiToggleButtonGroup: MuiToggleButtonGroupLightOverrides,
  MuiToolbar: MuiToolbarLightOverrides,
  MuiTooltip: MuiTooltipLightOverrides,
} as const;

export const NS_DARK_OVERRIDES = {
  BackstageContent: BackstageContentDarkOverrides,
  BackstageHeader: BackstageHeaderDarkOverrides,
  BackstageHeaderTabs: BackstageHeaderTabsDarkOverrides,
  BackstageItemCardGrid: BackstageItemCardGridDarkOverrides,
  CatalogReactUserListPicker: CatalogReactUserListPickerDarkOverrides,
  MuiAppBar: MuiAppBarDarkOverrides,
  MuiButton: MuiButtonDarkOverrides,
  MuiCard: MuiCardDarkOverrides,
  MuiCardActions: MuiCardActionsDarkOverrides,
  MuiCardContent: MuiCardContentDarkOverrides,
  MuiCardHeader: MuiCardHeaderDarkOverrides,
  MuiCheckbox: MuiCheckboxDarkOverrides,
  MuiChip: {
    ...MuiChipDarkOverrides,
    root: {
      ...MuiChipDarkOverrides.root,
      ...MuiChipInteractiveRootExtension,
    },
  },
  MuiDrawer: MuiDrawerDarkOverrides,
  MuiFormControlLabel: MuiFormControlLabelDarkOverrides,
  MuiFormHelperText: MuiFormHelperTextDarkOverrides,
  MuiIconButton: MuiIconButtonDarkOverrides,
  MuiInputBase: MuiInputBaseDarkOverrides,
  MuiInputLabel: MuiInputLabelDarkOverrides,
  MuiLink: MuiLinkDarkOverrides,
  MuiList: MuiListDarkOverrides,
  MuiListItem: MuiListItemDarkOverrides,
  MuiListItemIcon: MuiListItemIconDarkOverrides,
  MuiListItemText: MuiListItemTextDarkOverrides,
  MuiMenuItem: MuiMenuItemDarkOverrides,
  MuiNativeSelect: MuiNativeSelectDarkOverrides,
  MuiOutlinedInput: MuiOutlinedInputDarkOverrides,
  MuiPaper: MuiPaperDarkOverrides,
  MuiRadio: MuiRadioDarkOverrides,
  MuiSelect: MuiSelectDarkOverrides,
  MuiSwitch: MuiSwitchDarkOverrides,
  MuiTab: MuiTabDarkOverrides,
  MuiTable: MuiTableDarkOverrides,
  MuiTableCell: MuiTableCellDarkOverrides,
  MuiTableRow: MuiTableRowDarkOverrides,
  MuiTableSortLabel: MuiTableSortLabelDarkOverrides,
  MuiTabs: MuiTabsDarkOverrides,
  MuiTextField: MuiTextFieldDarkOverrides,
  MuiToggleButton: MuiToggleButtonDarkOverrides,
  MuiToggleButtonGroup: MuiToggleButtonGroupDarkOverrides,
  MuiToolbar: MuiToolbarDarkOverrides,
  MuiTooltip: MuiTooltipDarkOverrides,
} as const;
